import { Link, Text, HStack, Tooltip, Box, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { NavItem } from 'types/client/navigation';

import { route } from 'nextjs-routes';

import useIsMobile from 'lib/hooks/useIsMobile';
import { isInternalItem } from 'lib/hooks/useNavItems';
import IconSvg from 'ui/shared/IconSvg';
import NextLink from 'ui/shared/NextLink';

import LightningLabel, { LIGHTNING_LABEL_CLASS_NAME } from '../LightningLabel';
import NavLinkIcon from '../NavLinkIcon';
import useColors from '../useColors';
import useNavLinkStyleProps from '../useNavLinkStyleProps';
import { checkRouteHighlight } from '../utils';

type Props = {
  item: NavItem;
  onClick?: (e: React.MouseEvent) => void;
  isCollapsed?: boolean;
  isDisabled?: boolean;
  isSubmenu?: boolean;
  noHorizontalPadding?: boolean;
  isFirstLevel?: boolean;
};

const NavLink = ({ item, onClick, isCollapsed, isDisabled, isSubmenu, noHorizontalPadding, isFirstLevel }: Props) => {
  const isMobile = useIsMobile();
  const colors = useColors();
  const submenuColor = useColorModeValue('blackAlpha.800', 'whiteAlpha.900');

  const isInternalLink = isInternalItem(item);
  const href = isInternalLink ? route(item.nextRoute) : item.url;

  const isExpanded = isCollapsed === false;

  const styleProps = useNavLinkStyleProps({ isCollapsed, isExpanded, isActive: isInternalLink && item.isActive });
  const isXLScreen = useBreakpointValue({ base: false, xl: true });

  const isActive = isInternalLink && item.isActive;
  const itemProps = isSubmenu
    ? { ...styleProps.itemProps, color: isActive ? colors.text.active : submenuColor, _hover: { color: isActive ? colors.text.active : 'link_hovered' } }
    : styleProps.itemProps;
  const textProps = isSubmenu
    ? { ...styleProps.textProps, fontSize: 'sm', color: isActive ? colors.text.active : submenuColor }
    : isFirstLevel
      ? { ...styleProps.textProps, fontSize: 'sm', fontWeight: 400 }
      : styleProps.textProps;

  const isHighlighted = checkRouteHighlight(item);

  const content = (
    <Link
      href={ href }
      target={ isInternalLink ? '_self' : '_blank' }
      variant={ isActive ? 'primary' : 'secondary' }
      { ...itemProps }
      w={{ base: '100%', lg: isExpanded ? '100%' : '60px', xl: isCollapsed ? '60px' : '100%' }}
      display="flex"
      position="relative"
      px={{ base: noHorizontalPadding ? 0 : 2, lg: isExpanded ? 2 : '15px', xl: isCollapsed ? '15px' : 2 }}
      aria-label={ `${ item.text } link` }
      whiteSpace="nowrap"
      onClick={ onClick }
      _hover={{
        [`& *:not(.${ LIGHTNING_LABEL_CLASS_NAME }, .${ LIGHTNING_LABEL_CLASS_NAME } *)`]: {
          color: isDisabled ? 'inherit' : 'link_hovered',
        },
      }}
    >
      <Tooltip
        label={ item.text }
        hasArrow={ false }
        isDisabled={ isMobile || isCollapsed === false || (isCollapsed === undefined && isXLScreen) }
        placement="right"
        variant="nav"
        gutter={ 20 }
        color={ isInternalLink && item.isActive ? colors.text.active : colors.text.hover }
        margin={ 0 }
      >
        <HStack spacing={ 0 } overflow="hidden">
          { !isSubmenu && !noHorizontalPadding && <NavLinkIcon item={ item }/> }
          <Text { ...textProps } as="span" ml={ (isSubmenu || noHorizontalPadding) ? 0 : 3 }>
            <span>{ item.text }</span>
            { !isInternalLink && <IconSvg name="link_external" boxSize={ 3 } color={ isSubmenu ? (isActive ? colors.text.active : submenuColor) : 'icon_link_external' } verticalAlign="middle"/> }
          </Text>
          { isHighlighted && (
            <LightningLabel iconColor={ styleProps.itemProps.bgColor } isCollapsed={ isCollapsed }/>
          ) }
        </HStack>
      </Tooltip>
    </Link>
  );

  return (
    <Box as="li" listStyleType="none" w="100%">
      { isInternalLink ? (
        <NextLink href={ item.nextRoute } passHref legacyBehavior>
          { content }
        </NextLink>
      ) : content }
    </Box>
  );
};

export default React.memo(NavLink);
